import type { Currency } from './global.types';
import type { SubscriptionPlanNameEnum } from './users.types';

type BillingScheme = 'per_unit';
type Interval = 'year' | 'month';
type MetaData = unknown;
export type ShortNickName =
  | 'free1'
  | 'basic1'
  | 'basic12'
  | 'pro1'
  | 'pro12'
  | 'small1'
  | 'small12'
  | 'pro_max1'
  | 'pro_max12'
  | 'small_max1'
  | 'small_max12';
type PlanObject = 'plan';
type Product = 'prod_ItGdzTLtwBvOBI';
type UsageType = 'licensed';

export type SubscriptionPlan = {
  id: string;
  active: boolean;
  aggregate_usage: null;
  amount: number;
  amount_decimal: string;
  billing_scheme: BillingScheme;
  created: number;
  currency: Currency;
  interval: Interval;
  interval_count: number;
  livemode: boolean;
  metadata: MetaData;
  nickname: SubscriptionPlanNameEnum;
  shortNickname: ShortNickName;
  object: PlanObject;
  product: Product;
  tiers: null;
  tiers_mode: null;
  transform_usage: null;
  trial_period_days: null;
  usage_type: UsageType;
  features: {
    locks: Record<string, boolean>;
    counters: Record<
      SubscriptionCounterKeysEnum,
      { total: number | 'INFINITE'; units: number | 'INFINITE' }
    >;
  };
};

export type PaywallFeatureType = {
  title: string;
  description: string;
  Icon: React.FC<{ className: string }>;
};

export type LockContentType = {
  title: string | ((planName: string) => string);
  description:
    | string
    | ((planName: string, currentCount: number, limit: number) => string);
  backgroundImage?: string;
  primaryFeature?:
    | PaywallFeatureType
    | ((features: SubscriptionPlan['features']) => PaywallFeatureType);
  eventName?: string;
};

export enum SubscriptionLockKeysEnum {
  access_Accountant = 'access_Accountant',
  access_Web = 'access_Web',
  amortization = 'amortization',
  analytics = 'analytics',
  dataExport = 'dataExport',
  incomeTaxPayment = 'incomeTaxPayment',
  incomeTaxPrepayment = 'incomeTaxPrepayment',
  incomeTaxSubmission = 'incomeTaxSubmission',
  invoiceCustomization = 'invoiceCustomization',
  multipleBankAccounts = 'multipleBankAccounts',
  report_BWA = 'report_BWA',
  report_client = 'report_client',
  report_CODA = 'report_CODA',
  report_incomeTax = 'report_incomeTax',
  report_intraEU = 'report_intraEU',
  report_PL = 'report_PL',
  report_VAT = 'report_VAT',
  vat_details = 'vat_details',
  vat_export = 'vat_export',
  vatDetails = 'vatDetails',
  vatSubmission = 'vatSubmission',
  // NEW FEATURES
  viewCashAvailable = 'viewCashAvailable',
  createQuotes = 'createQuotes',
  createInstallmentInvoices = 'createInstallmentInvoices',
  createRecurringInvoices = 'createRecurringInvoices',
  talkToTaxCoach = 'talkToTaxCoach',
  createBankingSpaces = 'createBankingSpaces',
  receiveBankingCashBonus = 'receiveBankingCashBonus',
  viewTaxSubmissionItem = 'viewTaxSubmissionItem',
  viewTaxPaymentItem = 'viewTaxPaymentItem',
  createAmortizationExpense = 'createAmortizationExpense',
  capitalGainsTaxDe = 'capitalGainsTaxDe',
  viewWebHomePage = 'viewWebHomePage',
  viewWebBankingPage = 'viewWebBankingPage',
  viewWebTaxPage = 'viewWebTaxPage',
  viewWebExpensesPage = 'viewWebExpensesPage',
  viewWebRevenuesPage = 'viewWebRevenuesPage',
  viewWebAIAdvisorPage = 'viewWebAIAdvisorPage',
  viewWebDocRepositoryPage = 'viewWebDocRepositoryPage',
  viewWebExportPage = 'viewWebExportPage',
  viewWebExpensesAnalyticsPage = 'viewWebExpensesAnalyticsPage',
  viewWebRevenuesAnalyticsPage = 'viewWebRevenuesAnalyticsPage',
  viewWebTaxesAnalyticsPage = 'viewWebTaxesAnalyticsPage',
  personalizedTaxTips = 'personalizedTaxTips',
  addEmailAliases = 'addEmailAliases',
  excel = 'excel',
  original_documents = 'original_documents',
  ubl = 'ubl',
  pl = 'pl',
  coda = 'coda',
  pdf = 'pdf',
  amortisation_table = 'amortisation_table',
  datev = 'datev',
  bwa = 'bwa',
  // TAX FEATURE LOCKS
  submitVAT = 'submitVAT',
  submitClientList = 'submitClientList',
  submitIntracomListing = 'submitIntracomListing',
  submitIncomeTax = 'submitIncomeTax',
  submitEUR = 'submitEUR',
  submitTradeTax = 'submitTradeTax',
  payVAT = 'payVAT',
  payTradeTaxPrePayment = 'payTradeTaxPrePayment',
  payTradeTax = 'payTradeTax',
  payIncomeTaxPrePayment = 'payIncomeTaxPrePayment',
  payIncomeTax = 'payIncomeTax',
  paySocialContribution = 'paySocialContribution',
  payVATThreshold = 'payVATThreshold',
  payCorporateTax = 'payCorporateTax',
  payCorporateTaxPrePayment = 'payCorporateTaxPrePayment',
  submitSpecialVAT = 'submitSpecialVAT',
  paySpecialVAT = 'paySpecialVAT',
  submitUSTE = 'submitUSTE',
  payUSTE = 'payUSTE',

  // OTHERS
  freePhysicalCard = 'freePhysicalCard',
  revenuesAnalytics = 'revenuesAnalytics',
  expensesAnalytics = 'expensesAnalytics',
  taxesAnalytics = 'taxesAnalytics',
}

export enum SubscriptionCounterKeysEnum {
  documents = 'documents',
  bankAccounts = 'bankAccounts',
  taxAdvisorMonthlyMessages = 'taxAdvisorMonthlyMessages',
  monthlyExpenses = 'monthlyExpenses',
}
